import React, { useCallback, useEffect, useState } from 'react';

import Button from 'src/components/buttons/Button';
import ToastMessage from 'src/components/toastMessage';
import { triggerBingAnalytics } from 'src/services/apiRequests/commerce/bingAnalytics';
import { ICommerceAsset } from 'src/services/responseTypes/commerceResponse';
import { colors } from 'src/styles/colors';
import { MediumFont, RegularFont } from 'src/styles/font';
import { CommerceCardProps } from 'src/types/commerce';
import {
  commerceCTA,
  commerceToast
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getSessionStorage } from 'src/utils/browserStorageHandler';
import {
  isServer,
  removeCommerceWishlistedProduct,
  setCommerceWishlistData
} from 'src/utils/common';
import { Commerce } from 'src/utils/constants/CommerceConstants';
import { COMMERCE_WISHLISTED_PRODUCTS } from 'src/utils/constants/localStorageConstants';
import { DEVICE_WIDTH_CONSTANTS } from 'src/utils/constants/utilConstants';

import {
  descriptionStyling,
  DiscountedPrice,
  FigureWrapper,
  PriceDetailsWrapper,
  textContainer,
  buyNow,
  iconStyling,
  text,
  cardContainer,
  descriptionWrapper,
  title,
  OriginalPrice
} from './styles';
import { MIXPANEL_WISHLIST } from '../../../utils/analytics/mixpanel/mixpanelConstants';

const CommerceCard: React.FC<CommerceCardProps> = ({
  Id = '',
  onPlayer = false,
  wishlisted = false,
  showWishlist = false,
  translations = {},
  setPlayerShowToast,
  setSideBarData,
  setCommerceWishlistedProductsLength,
  ...props
}) => {
  const {
    id = '',
    url = '',
    price = '',
    discountPrice = '',
    currencySymbol = '₹',
    visibilityFeedbackUrl = '',
    pingUrlBase = '',
    urlPingSuffix = '',
    seller: { name: sellerName = '' } = {},
    itemOffered: { contentImageUrl = '', name = '', description = '' } = {}
  } = props;

  const {
    plex: {
      ContextualCommerce_WishlistAddedToast = '',
      ContextualCommerce_WishlistRemoveToast = ''
    } = {}
  } = translations || {};

  const isMediaLg =
    window?.innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200;
  const isMedialXl =
    window?.innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1800;

  const [markAsWishlisted, setMarkAsWishlisted] = useState<boolean>(wishlisted);
  const [showToast, setShowToast] = useState<boolean>(false);

  useEffect(() => {
    setMarkAsWishlisted(wishlisted);
  }, [wishlisted]);

  const buyClickHandler = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (e && !isServer()) {
      e.stopPropagation();
      window?.videoPlayer?.pause();
      window.open(url, '_blank', 'noopener,noreferrer');
      commerceCTA(Commerce.TITLE.replace('<name>', sellerName));
      // Call pingURLBase + urlPingSuffix if customer clicks on Buy CTA
      if (pingUrlBase && urlPingSuffix) {
        triggerBingAnalytics(`${pingUrlBase}${urlPingSuffix}`);
      }
    }
  }, []);

  /* eslint-disable cyclomatic-complexity/zee-codeBlockComplexity */
  const wishlistHandler = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e && !isServer()) {
        e.stopPropagation();
        const productWishlisted = e.currentTarget.dataset.wishlisted;
        setMarkAsWishlisted(
          productWishlisted === Commerce.FALSE ? true : false
        );
        e.currentTarget.dataset.wishlisted =
          productWishlisted === Commerce.TRUE ? Commerce.FALSE : Commerce.TRUE;
        commerceCTA(MIXPANEL_WISHLIST);
        if (productWishlisted === Commerce.FALSE) {
          if (onPlayer && setPlayerShowToast) {
            setPlayerShowToast(true);
          } else {
            setShowToast(true);
          }
          setCommerceWishlistData(props);
          commerceToast(ContextualCommerce_WishlistAddedToast);
        } else {
          if (onPlayer && setPlayerShowToast) {
            setPlayerShowToast(false);
          } else {
            setShowToast(false);
          }
          removeCommerceWishlistedProduct(id);
          commerceToast(ContextualCommerce_WishlistRemoveToast);
        }
        if (showWishlist) {
          setSideBarData((prevState: Array<ICommerceAsset>) =>
            prevState.filter((product: ICommerceAsset) => product?.id !== id)
          );
        }
        const productsWishlisted =
          getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || [];
        setCommerceWishlistedProductsLength(productsWishlisted.length);
      }
    },
    [showWishlist]
  );

  return (
    <>
      <div
        css={cardContainer(onPlayer)}
        id={Id}
        data-visibilityFeedbackUrl={visibilityFeedbackUrl}>
        <FigureWrapper>
          <img
            title={Commerce.TITLE.replace('<name>', sellerName)}
            src={contentImageUrl}
            loading="lazy"
            crossOrigin="anonymous"
            alt={name}
          />
        </FigureWrapper>
        <div css={descriptionWrapper(onPlayer)}>
          <div css={descriptionStyling(onPlayer)}>
            <div css={textContainer(onPlayer)}>
              <span css={title(onPlayer)} title={name}>
                {name}
              </span>
              <span css={text(onPlayer)} title={description}>
                {description}
              </span>
            </div>
            {discountPrice || price ? (
              <PriceDetailsWrapper>
                <DiscountedPrice>{`${currencySymbol} ${
                  discountPrice || price
                }`}</DiscountedPrice>
                {discountPrice ? (
                  <OriginalPrice>{`${currencySymbol} ${price}`}</OriginalPrice>
                ) : (
                  ''
                )}
              </PriceDetailsWrapper>
            ) : (
              ''
            )}
            {!onPlayer ? (
              <Button
                callback={buyClickHandler}
                customStyles={buyNow}
                title={Commerce.TITLE.replace('<name>', sellerName)}>
                {Commerce.TITLE.replace('<name>', sellerName)}
              </Button>
            ) : (
              ''
            )}
          </div>
          <span
            css={iconStyling(markAsWishlisted)}
            title={
              markAsWishlisted
                ? Commerce.REMOVE_WISHLIST
                : Commerce.ADD_WISHLIST
            }
            data-wishlisted={markAsWishlisted}
            onClick={wishlistHandler}
          />
        </div>
      </div>
      {showToast && (
        <ToastMessage
          width="328px"
          textAlign="left"
          maxWidth="352px"
          padding="12px 16px"
          duration={3000}
          fontWeight={isMedialXl ? 400 : 500}
          fontFamily={isMedialXl ? RegularFont : MediumFont}
          fontSize={isMediaLg ? (isMedialXl ? 16 : 14) : 12}
          lineHeight={isMediaLg ? (isMedialXl ? 24 : 20) : 18}
          textColor={colors.PrimaryWhiteColor}
          margin={isMediaLg ? '0 0 0 32px' : '0 auto'}
          backgroundColor={colors.AccentDarkGrayColor}
          message={ContextualCommerce_WishlistAddedToast}
        />
      )}
    </>
  );
};

export default CommerceCard;
